(function(globalScope) {
  'use strict';

  var WS_CONNECTING = 0;
  var WS_OPEN = 1;
  var WS_CLOSING = 2;
  var WS_CLOSED = 3;

  var DEFAULTS = {
    minReconnectionDelay: 1000,
    maxReconnectionDelay: 10000,
    reconnectionDelayGrowFactor: 1.5,
    minUptime: 5000,
    connectionTimeout: 4000,
    maxRetries: Infinity,
    maxEnqueuedMessages: Infinity,
    startClosed: false,
    debug: false,
    WebSocket: globalScope.WebSocket
  };

  function mergeOptions(options) {
    var merged = {};
    var key;
    for (key in DEFAULTS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULTS, key)) {
        merged[key] = DEFAULTS[key];
      }
    }

    if (!options) {
      return merged;
    }

    for (key in options) {
      if (Object.prototype.hasOwnProperty.call(options, key) && typeof options[key] !== 'undefined') {
        merged[key] = options[key];
      }
    }

    return merged;
  }

  function dataSize(value) {
    if (typeof value === 'string') {
      return value.length;
    }

    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      return value.size;
    }

    if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
      return value.byteLength;
    }

    if (
      typeof ArrayBuffer !== 'undefined' &&
      typeof ArrayBuffer.isView === 'function' &&
      ArrayBuffer.isView(value)
    ) {
      return value.byteLength;
    }

    return 0;
  }

  function isPromiseLike(value) {
    return value && typeof value.then === 'function';
  }

  function ReconnectingWebSocket(url, protocols, options) {
    this._urlInput = url;
    this._protocols = protocols;
    this._options = mergeOptions(options);

    if (!this._options.WebSocket) {
      throw new Error('No WebSocket implementation available in this environment.');
    }

    this._ws = null;
    this._retryCount = -1;
    this._shouldReconnect = !this._options.startClosed;
    this._closeCalled = false;
    this._connectLock = false;
    this._binaryType = 'blob';

    this._messageQueue = [];
    this._queuedBytes = 0;

    this._connectTimer = null;
    this._connectionTimeoutTimer = null;
    this._uptimeTimer = null;

    this._listeners = {
      open: [],
      message: [],
      error: [],
      close: []
    };

    this.onopen = null;
    this.onmessage = null;
    this.onerror = null;
    this.onclose = null;

    this._handleOnline = this._handleOnline.bind(this);
    this._onOpen = this._onOpen.bind(this);
    this._onMessage = this._onMessage.bind(this);
    this._onError = this._onError.bind(this);
    this._onClose = this._onClose.bind(this);

    if (typeof globalScope.addEventListener === 'function') {
      globalScope.addEventListener('online', this._handleOnline);
    }

    this._connect();
  }

  ReconnectingWebSocket.CONNECTING = WS_CONNECTING;
  ReconnectingWebSocket.OPEN = WS_OPEN;
  ReconnectingWebSocket.CLOSING = WS_CLOSING;
  ReconnectingWebSocket.CLOSED = WS_CLOSED;

  ReconnectingWebSocket.prototype.CONNECTING = WS_CONNECTING;
  ReconnectingWebSocket.prototype.OPEN = WS_OPEN;
  ReconnectingWebSocket.prototype.CLOSING = WS_CLOSING;
  ReconnectingWebSocket.prototype.CLOSED = WS_CLOSED;

  Object.defineProperty(ReconnectingWebSocket.prototype, 'binaryType', {
    get: function() {
      if (this._ws) {
        return this._ws.binaryType;
      }
      return this._binaryType;
    },
    set: function(value) {
      this._binaryType = value;
      if (this._ws) {
        this._ws.binaryType = value;
      }
    }
  });

  Object.defineProperty(ReconnectingWebSocket.prototype, 'retryCount', {
    get: function() {
      return Math.max(this._retryCount, 0);
    }
  });

  Object.defineProperty(ReconnectingWebSocket.prototype, 'bufferedAmount', {
    get: function() {
      var wsBuffered = this._ws ? this._ws.bufferedAmount : 0;
      return this._queuedBytes + wsBuffered;
    }
  });

  Object.defineProperty(ReconnectingWebSocket.prototype, 'extensions', {
    get: function() {
      return this._ws ? this._ws.extensions : '';
    }
  });

  Object.defineProperty(ReconnectingWebSocket.prototype, 'protocol', {
    get: function() {
      return this._ws ? this._ws.protocol : '';
    }
  });

  Object.defineProperty(ReconnectingWebSocket.prototype, 'readyState', {
    get: function() {
      if (this._ws) {
        return this._ws.readyState;
      }
      return this._shouldReconnect ? WS_CONNECTING : WS_CLOSED;
    }
  });

  Object.defineProperty(ReconnectingWebSocket.prototype, 'url', {
    get: function() {
      if (this._ws) {
        return this._ws.url;
      }
      return typeof this._urlInput === 'string' ? this._urlInput : '';
    }
  });

  ReconnectingWebSocket.prototype.addEventListener = function(type, listener) {
    if (!this._listeners[type] || typeof listener !== 'function') {
      return;
    }
    this._listeners[type].push(listener);
  };

  ReconnectingWebSocket.prototype.removeEventListener = function(type, listener) {
    if (!this._listeners[type]) {
      return;
    }

    this._listeners[type] = this._listeners[type].filter(function(candidate) {
      return candidate !== listener;
    });
  };

  ReconnectingWebSocket.prototype.dispatchEvent = function(event) {
    if (!event || !event.type || !this._listeners[event.type]) {
      return false;
    }

    var listeners = this._listeners[event.type].slice();
    for (var i = 0; i < listeners.length; i += 1) {
      try {
        listeners[i].call(this, event);
      } catch (err) {
        setTimeout(function(error) {
          throw error;
        }.bind(null, err), 0);
      }
    }

    return true;
  };

  ReconnectingWebSocket.prototype.close = function(code, reason) {
    if (typeof code === 'undefined') {
      code = 1000;
    }

    this._closeCalled = true;
    this._shouldReconnect = false;
    this._clearTimers();

    if (typeof globalScope.removeEventListener === 'function') {
      globalScope.removeEventListener('online', this._handleOnline);
    }

    if (this._ws && this._ws.readyState !== WS_CLOSED) {
      this._debug('close', code, reason || '');
      this._ws.close(code, reason);
    }
  };

  ReconnectingWebSocket.prototype.reconnect = function(code, reason) {
    if (typeof code === 'undefined') {
      code = 1000;
    }

    this._debug('reconnect');
    this._closeCalled = false;
    this._shouldReconnect = true;
    this._retryCount = -1;
    this._clearTimers();

    if (this._ws && this._ws.readyState !== WS_CLOSED) {
      this._teardownSocket();
      try {
        this._ws.close(code, reason);
      } catch (err) {
        this._debug('reconnect close error', err);
      }
      this._ws = null;
    }

    this._connect();
  };

  ReconnectingWebSocket.prototype.send = function(data) {
    if (this._ws && this._ws.readyState === WS_OPEN) {
      this._ws.send(data);
      return;
    }

    var maxQueue = this._options.maxEnqueuedMessages;
    if (this._messageQueue.length >= maxQueue) {
      return;
    }

    this._messageQueue.push(data);
    this._queuedBytes += dataSize(data);
  };

  ReconnectingWebSocket.prototype._debug = function() {
    if (!this._options.debug || typeof console === 'undefined') {
      return;
    }

    var args = Array.prototype.slice.call(arguments);
    args.unshift('RWS>');
    console.log.apply(console, args);
  };

  ReconnectingWebSocket.prototype._emit = function(type, event) {
    var propertyHandler = this['on' + type];

    if (typeof propertyHandler === 'function') {
      propertyHandler.call(this, event);
    }

    this.dispatchEvent(event);
  };

  ReconnectingWebSocket.prototype._clearTimers = function() {
    if (this._connectTimer) {
      clearTimeout(this._connectTimer);
      this._connectTimer = null;
    }

    if (this._connectionTimeoutTimer) {
      clearTimeout(this._connectionTimeoutTimer);
      this._connectionTimeoutTimer = null;
    }

    if (this._uptimeTimer) {
      clearTimeout(this._uptimeTimer);
      this._uptimeTimer = null;
    }
  };

  ReconnectingWebSocket.prototype._teardownSocket = function() {
    if (!this._ws) {
      return;
    }

    this._ws.removeEventListener('open', this._onOpen);
    this._ws.removeEventListener('message', this._onMessage);
    this._ws.removeEventListener('error', this._onError);
    this._ws.removeEventListener('close', this._onClose);
  };

  ReconnectingWebSocket.prototype._resolveUrl = function() {
    if (typeof this._urlInput === 'string') {
      return Promise.resolve(this._urlInput);
    }

    if (typeof this._urlInput === 'function') {
      var value = this._urlInput();
      if (typeof value === 'string') {
        return Promise.resolve(value);
      }
      if (isPromiseLike(value)) {
        return value;
      }
    }

    return Promise.reject(new Error('Invalid WebSocket URL configuration.'));
  };

  ReconnectingWebSocket.prototype._nextDelay = function() {
    if (this._retryCount <= 0) {
      return 0;
    }

    var minDelay = this._options.minReconnectionDelay;
    var maxDelay = this._options.maxReconnectionDelay;
    var factor = this._options.reconnectionDelayGrowFactor;

    var delay = minDelay * Math.pow(factor, this._retryCount - 1);
    if (delay > maxDelay) {
      delay = maxDelay;
    }

    var jitter = Math.random() * Math.min(250, delay * 0.15);
    return Math.floor(delay + jitter);
  };

  ReconnectingWebSocket.prototype._connect = function() {
    var self = this;

    if (!this._shouldReconnect || this._connectLock) {
      return;
    }

    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      this._debug('offline: waiting for online event');
      return;
    }

    var maxRetries = this._options.maxRetries;
    if (this._retryCount >= maxRetries) {
      this._debug('max retries reached', this._retryCount);
      return;
    }

    this._retryCount += 1;
    this._connectLock = true;

    var delay = this._nextDelay();
    this._debug('connect attempt', this._retryCount, 'delay', delay);

    this._connectTimer = setTimeout(function() {
      self._resolveUrl()
        .then(function(url) {
          if (self._closeCalled || !self._shouldReconnect) {
            self._connectLock = false;
            return;
          }

          var SocketCtor = self._options.WebSocket;
          self._teardownSocket();

          self._ws = self._protocols ? new SocketCtor(url, self._protocols) : new SocketCtor(url);
          self._ws.binaryType = self._binaryType;

          self._ws.addEventListener('open', self._onOpen);
          self._ws.addEventListener('message', self._onMessage);
          self._ws.addEventListener('error', self._onError);
          self._ws.addEventListener('close', self._onClose);

          self._connectLock = false;
          self._connectionTimeoutTimer = setTimeout(function() {
            if (self._ws && self._ws.readyState === WS_CONNECTING) {
              self._debug('connection timeout');
              self._ws.close(4000, 'timeout');
            }
          }, self._options.connectionTimeout);
        })
        .catch(function(err) {
          self._connectLock = false;
          self._debug('url resolution failed', err);
          self._scheduleReconnect();
        });
    }, delay);
  };

  ReconnectingWebSocket.prototype._scheduleReconnect = function() {
    if (!this._shouldReconnect) {
      return;
    }
    this._connect();
  };

  ReconnectingWebSocket.prototype._flushQueue = function() {
    if (!this._ws || this._ws.readyState !== WS_OPEN || !this._messageQueue.length) {
      return;
    }

    for (var i = 0; i < this._messageQueue.length; i += 1) {
      this._ws.send(this._messageQueue[i]);
    }

    this._messageQueue = [];
    this._queuedBytes = 0;
  };

  ReconnectingWebSocket.prototype._onOpen = function(event) {
    this._debug('open');

    if (this._connectionTimeoutTimer) {
      clearTimeout(this._connectionTimeoutTimer);
      this._connectionTimeoutTimer = null;
    }

    var self = this;
    if (this._uptimeTimer) {
      clearTimeout(this._uptimeTimer);
    }

    this._uptimeTimer = setTimeout(function() {
      self._retryCount = 0;
    }, this._options.minUptime);

    this._flushQueue();
    this._emit('open', event);
  };

  ReconnectingWebSocket.prototype._onMessage = function(event) {
    this._emit('message', event);
  };

  ReconnectingWebSocket.prototype._onError = function(event) {
    this._debug('error', event && event.message ? event.message : event);
    this._emit('error', event);
  };

  ReconnectingWebSocket.prototype._onClose = function(event) {
    this._debug('close', event && event.code, event && event.reason);

    this._teardownSocket();

    if (this._connectionTimeoutTimer) {
      clearTimeout(this._connectionTimeoutTimer);
      this._connectionTimeoutTimer = null;
    }

    if (this._uptimeTimer) {
      clearTimeout(this._uptimeTimer);
      this._uptimeTimer = null;
    }

    this._ws = null;
    this._emit('close', event);

    if (this._shouldReconnect) {
      this._scheduleReconnect();
    }
  };

  ReconnectingWebSocket.prototype._handleOnline = function() {
    if (!this._shouldReconnect) {
      return;
    }

    if (!this._ws || this._ws.readyState === WS_CLOSED) {
      this._debug('online event: reconnecting');
      this._connect();
    }
  };

  globalScope.ReconnectingWebSocket = ReconnectingWebSocket;
})(window);
